import React, { FunctionComponent, useRef, useState, useEffect } from 'react';
import AppButton from 'components/AppButton/AppButton';
import { CSVLink } from 'react-csv';
import { DownloadIcon, toaster } from 'evergreen-ui';
import { FeedbackList } from '../../../typings/target_group';
import LinearProgress from '@material-ui/core/LinearProgress';
import {
    useLazyGetFeedbackQuery,
    useLazyGetParticipantsQuery,
} from 'store/api/participants';
import { retryWithBackoff } from 'utils/retry';

export interface TargetGroupFeedbackDownloadProps {
    companyId: string;
    companyName: string;
    groupId: string;
    assessmentName: string | undefined;
    targetGroupName: string;
    assessmentLanguage: string | undefined;
    totalParticipants: number;
}

const TargetGroupFeedbackDownload: FunctionComponent<
    TargetGroupFeedbackDownloadProps
> = (props) => {
    const {
        companyId,
        companyName,
        groupId,
        assessmentName,
        assessmentLanguage,
        targetGroupName,
        totalParticipants,
    } = props;

    const [getParticipants] = useLazyGetParticipantsQuery();
    const [getFeedback] = useLazyGetFeedbackQuery();

    const csvLink = useRef<
        CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
    >(null);
    const isRunningRef = useRef<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isPaused, setIsPaused] = useState<boolean>(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
    const [isDone, setIsDone] = useState(false);
    const [processedCount, setProcessedCount] = useState<number>(0);
    const [resumeToken, setResumeToken] = useState<string | null>(null);
    const [resumeFeedback, setResumeFeedback] = useState<FeedbackList>({});

    const [feedbackCsvData, setFeedbackCsvData] = useState<
        {
            [p: string]: string | number | undefined;
            'Scan name': string | undefined;
            'Group name': string;
            'Candidate ID': string;
        }[]
    >([]);

    const experienceQuestion =
        assessmentLanguage === 'nl-NL'
            ? 'Beschrijf hoe je ervaring was tijdens het uitvoeren van deze scan.'
            : assessmentLanguage === 'fr-FR'
            ? 'Veuillez décrire votre expérience lors de ce scan.'
            : 'Please describe your experience taking this scan.';
    const suggestionsQuestion =
        assessmentLanguage === 'nl-NL'
            ? 'Is er nog iets dat je met ons of ' +
              companyName +
              ' zou willen delen?'
            : assessmentLanguage === 'fr-FR'
            ? 'Y a-t-il quelque chose que vous souhaitez partager avec nous ou avec ' +
              companyName +
              ' ?'
            : 'Is there anything you want to share with us or ' +
              companyName +
              '?';
    const feedbackRate =
        assessmentLanguage === 'nl-NL'
            ? 'Hoe zou je je ervaring tijdens het uitvoeren van de scan beoordelen?'
            : assessmentLanguage === 'fr-FR'
            ? 'Comment évalueriez-vous votre expérience lors de ce scan ?'
            : 'How would you rate your experience doing this scan?';

    function wait(ms: number) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    const runDownload = async (
        startToken: string,
        startFeedback: FeedbackList,
        isResume: boolean,
    ) => {
        if (isRunningRef.current) return;
        isRunningRef.current = true;

        try {
            const limit = 80;
            const feedback: FeedbackList = { ...startFeedback };

            setIsSaving(true);
            setIsPaused(false);
            setIsDone(false);
            setFeedbackCsvData([]);
            if (!isResume) setProcessedCount(0);

            let nextToken = startToken;
            let lastAttemptedToken = startToken;

            try {
                // eslint-disable-next-line no-constant-condition
                while (true) {
                    const tokenForThisPage = nextToken;
                    lastAttemptedToken = tokenForThisPage;
                    const { participants, feedbackList, nextNextToken } =
                        await retryWithBackoff(async () => {
                            const participantsPage = await getParticipants({
                                companyIdentifier: companyId,
                                groupId,
                                limit,
                                includeResults: false,
                                includeTotal: false,
                                nextToken: tokenForThisPage,
                            }).unwrap();

                            const feedbackForPage = await getFeedback({
                                companyIdentifier: companyId,
                                groupId,
                                userIds: participantsPage.participants.map(
                                    (p) => p.user_id,
                                ),
                            }).unwrap();

                            return {
                                participants: participantsPage.participants,
                                feedbackList: feedbackForPage,
                                nextNextToken: participantsPage.nextToken,
                            };
                        });

                    Object.assign(feedback, feedbackList);
                    setProcessedCount((prev) => prev + participants.length);

                    const next = nextNextToken ?? '';
                    if (!next) break;
                    nextToken = next;
                    await wait(1000);
                }
            } catch (e) {
                console.error('Error getting feedback', e);
                setResumeToken(lastAttemptedToken);
                setResumeFeedback(feedback);
                setIsSaving(false);
                setIsPaused(true);
                toaster.warning(
                    'Download paused. Click "Continue download" to resume.',
                );
                return;
            }

            setResumeToken(null);
            setResumeFeedback({});

            if (Object.keys(feedback).length) {
                setFeedbackCsvData(
                    Object.keys(feedback).map((userId) => ({
                        'Scan name': assessmentName,
                        'Group name': targetGroupName,
                        'Candidate ID': userId,
                        'Report generated at (UTC)':
                            feedback[userId].reportGeneratedAt || '-',
                        [experienceQuestion]: feedback[userId].experienceDescription
                            ? feedback[userId].experienceDescription
                            : '-',
                        [suggestionsQuestion]: feedback[userId].improveSuggestion
                            ? feedback[userId].improveSuggestion
                            : '-',
                        [feedbackRate]: feedback[userId].feedbackRate
                            ? feedback[userId].feedbackRate
                            : '-',
                    })),
                );
            }

            if (!Object.keys(feedback).length) {
                setIsButtonDisabled(true);
            }
            setIsDone(true);
        } finally {
            isRunningRef.current = false;
        }
    };

    const downloadFeedback = () => {
        void runDownload('', {}, false);
    };
    const continueDownload = () => {
        if (resumeToken !== null) {
            void runDownload(resumeToken, resumeFeedback, true);
        }
    };

    useEffect(() => {
        if (feedbackCsvData.length && isSaving) {
            csvLink?.current?.link.click();
            setIsSaving(false);
            setProcessedCount(0);
        }
    }, [feedbackCsvData, isSaving]);

    const progress = totalParticipants
        ? Math.min((processedCount / totalParticipants) * 100, 100)
        : 0;

    return (
        <div aria-label="download feedback button">
            {isSaving && !isDone && !isPaused && (
                <div>
                    <LinearProgress variant="determinate" value={progress} />
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                        Downloading feedback…
                    </div>
                </div>
            )}
            {isPaused && (
                <div>
                    <div style={{ fontSize: 12, marginBottom: 8 }}>
                        Download paused at {processedCount}
                        {totalParticipants ? ` / ${totalParticipants}` : ''}{' '}
                        candidates.
                    </div>
                    <AppButton
                        justifyContent={'flex-start'}
                        width={'auto'}
                        icon={DownloadIcon}
                        isDisabled={false}
                        onClick={continueDownload}>
                        Continue download
                    </AppButton>
                </div>
            )}
            {!isPaused && (isDone || !isSaving) && (
                <AppButton
                    justifyContent={'flex-start'}
                    width={'auto'}
                    icon={DownloadIcon}
                    isDisabled={isSaving || isButtonDisabled}
                    onClick={() => downloadFeedback()}>
                    Download all feedback
                </AppButton>
            )}
            <CSVLink
                data={feedbackCsvData}
                filename={companyName + '-' + targetGroupName + '-feedback.csv'}
                className="hidden"
                ref={csvLink}
                target={'_blank'}
            />
        </div>
    );
};

export default TargetGroupFeedbackDownload;
